/**
 * Public Receipt Verification page — /verify/receipt/:receiptId
 *
 * Target of the QR code on every printed PDF receipt. Anyone with the PDF
 * can scan the code (or paste the URL), drop the PDF here, and confirm:
 *
 *   - the receipt exists on the issuer's ledger
 *   - the SHA-256 hash of the PDF they hold matches the hash on file
 *
 * No login required; no identities exposed. Just a yes/no authenticity
 * answer with a hash to compare.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getAppMarket } from '../../services/MarketService';
import { receiptsService } from '../../services/api';

interface Verify {
    receiptId: string;
    contentHash: string;
    amount: number;
    paidDate: string;
    periodYM: string;
    issuedAt: string;
}

export default function VerifyReceipt() {
    const { receiptId } = useParams<{ receiptId: string }>();
    // F-308: US receipt = $ / en-US and no UK statute attribution.
    const isTexas = getAppMarket() === 'texas';
    const [record, setRecord] = useState<Verify | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [pdfHash, setPdfHash] = useState<string | null>(null);
    const [hashing, setHashing] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!receiptId) return;
        receiptsService.verify(receiptId)
            .then((res: any) => setRecord(res.data.data))
            .catch((err: any) => setError(err?.response?.status === 404
                ? 'No receipt with that ID exists in our records.'
                : err?.response?.data?.message || 'Verification lookup failed'))
            .finally(() => setLoading(false));
    }, [receiptId]);

    const handleFile = async (file: File | null) => {
        if (!file) return;
        setPdfHash(null);
        setHashing(true);
        try {
            const buf = await file.arrayBuffer();
            const digest = await crypto.subtle.digest('SHA-256', buf);
            const hex = Array.from(new Uint8Array(digest))
                .map(b => b.toString(16).padStart(2, '0')).join('');
            setPdfHash(hex);
        } catch (err: any) {
            setError('Could not read the file: ' + err.message);
        } finally {
            setHashing(false);
        }
    };

    const matches = pdfHash && record?.contentHash && pdfHash.toLowerCase() === record.contentHash.toLowerCase();

    const fmtPeriod = (ym?: string) => {
        if (!ym) return '';
        const [y, m] = ym.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-12 px-4">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-5 text-white">
                        <div className="flex items-center">
                            <i className="fas fa-shield-check text-3xl mr-4"></i>
                            <div>
                                <h1 className="text-xl font-bold">Receipt Verification</h1>
                                <p className="text-xs text-emerald-100">Cryptographic proof of payment</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {loading && (
                            <div className="text-center py-10 text-gray-500">
                                <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
                                <p>Looking up receipt…</p>
                            </div>
                        )}

                        {error && !record && (
                            <div className="bg-red-50 border border-red-100 rounded-xl p-5 flex items-start">
                                <i className="fas fa-times-circle text-2xl text-red-600 mr-4"></i>
                                <div>
                                    <p className="font-bold text-red-900">Receipt not found</p>
                                    <p className="text-sm text-red-700 mt-1">{error}</p>
                                    <p className="text-xs text-red-600 mt-3 font-mono">Receipt ID: {receiptId}</p>
                                </div>
                            </div>
                        )}

                        {record && (
                            <>
                                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-6 flex items-center">
                                    <i className="fas fa-check-circle text-2xl text-emerald-600 mr-3"></i>
                                    <p className="text-sm text-emerald-900"><strong>Receipt exists on the ledger.</strong> Drop the PDF below to confirm the hash matches.</p>
                                </div>

                                <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm mb-6">
                                    <Field label="Receipt ID"    value={record.receiptId} mono />
                                    <Field label="Amount"        value={`${isTexas ? '$' : '£'}${record.amount.toLocaleString(isTexas ? 'en-US' : 'en-GB', { minimumFractionDigits: 2 })}`} />
                                    <Field label="Date paid"     value={new Date(record.paidDate).toLocaleDateString(isTexas ? 'en-US' : 'en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} />
                                    <Field label="Billing period" value={fmtPeriod(record.periodYM)} />
                                    <Field label="Issued"        value={new Date(record.issuedAt).toLocaleString(isTexas ? 'en-US' : 'en-GB')} />
                                    <Field label="Hash on file"  value={record.contentHash} mono small />
                                </dl>

                                {/* Hash check */}
                                <div className="border-t pt-5">
                                    <h2 className="font-bold text-gray-900 mb-2">Confirm the document you hold</h2>
                                    <p className="text-xs text-gray-500 mb-4">Drop the PDF here. Hashing happens in your browser — the file never leaves this page.</p>

                                    <div
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0] || null); }}
                                        onClick={() => fileRef.current?.click()}
                                        className="border-2 border-dashed border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/30 rounded-xl p-6 text-center cursor-pointer transition"
                                    >
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) => handleFile(e.target.files?.[0] || null)}
                                            className="hidden"
                                        />
                                        <i className="fas fa-file-pdf text-3xl text-gray-300 mb-2"></i>
                                        <p className="text-sm text-gray-600">{hashing ? 'Hashing…' : 'Drop or click to choose the PDF'}</p>
                                    </div>

                                    {pdfHash && (
                                        <div className={`mt-5 rounded-xl p-4 ${matches ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                                            <div className="flex items-center mb-2">
                                                <i className={`fas ${matches ? 'fa-check-circle text-emerald-600' : 'fa-times-circle text-red-600'} text-xl mr-3`}></i>
                                                <p className={`font-bold ${matches ? 'text-emerald-900' : 'text-red-900'}`}>
                                                    {matches ? 'Authenticity confirmed' : 'Hash does NOT match'}
                                                </p>
                                            </div>
                                            <p className="text-xs text-gray-600">
                                                {matches
                                                    ? 'The document you uploaded is identical to the one issued by the landlord. It has not been modified.'
                                                    : 'The document has been altered since issue, or you uploaded a different file. Compare hashes below.'}
                                            </p>
                                            <p className="text-[10px] font-mono text-gray-500 mt-3 break-all">
                                                <span className="font-semibold">Your file:</span> {pdfHash}
                                            </p>
                                            <p className="text-[10px] font-mono text-gray-500 break-all">
                                                <span className="font-semibold">On file:</span> {record.contentHash}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="bg-gray-50 px-6 py-3 text-center text-xs text-gray-500 border-t">
                        Powered by Landlord Pro — {isTexas
                            ? 'Compliance-grade payment evidence for Texas rental properties'
                            : "Compliance-grade payment evidence for the Renters' Rights Act 2025"}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, mono, small }: { label: string; value: string; mono?: boolean; small?: boolean }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</dt>
            <dd className={`mt-0.5 text-gray-900 ${mono ? 'font-mono' : ''} ${small ? 'text-[10px] break-all' : ''}`}>{value}</dd>
        </div>
    );
}
